import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../AuthContext';

const API_BASE =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.insightos.co.uk'
    : 'https://api.insightos.co.uk';
const OTP_LENGTH = 6;

const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.6c-.2 1.3-1.6 3.8-5.6 3.8-3.4 0-6.1-2.8-6.1-6.2S8.6 5.5 12 5.5c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3 14.7 2 12 2 6.5 2 2.1 6.4 2.1 12S6.5 22 12 22c5.7 0 9.5-4 9.5-9.7 0-.7-.1-1.2-.2-1.7H12z" />
  </svg>
);

const MicrosoftLogo = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#F25022" d="M2 2h9v9H2z" />
    <path fill="#7FBA00" d="M13 2h9v9h-9z" />
    <path fill="#00A4EF" d="M2 13h9v9H2z" />
    <path fill="#FFB900" d="M13 13h9v9h-9z" />
  </svg>
);

const AuthenticatorBox = () => (
  <div className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-inner shadow-black/20">
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-400">Authenticator app</p>
        <p className="mt-1 text-sm text-slate-200">Use Google Authenticator or Microsoft Authenticator</p>
      </div>
      <div className="flex items-center gap-2 rounded-full border border-white/10 bg-slate-950/70 px-3 py-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/5">
          <GoogleLogo />
        </span>
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/5">
          <MicrosoftLogo />
        </span>
      </div>
    </div>
  </div>
);

export default function MFA() {
  const [authCode, setAuthCode] = useState('');
  const [setupCode, setSetupCode] = useState('');
  const [mode, setMode] = useState('loading');
  const [setupData, setSetupData] = useState(null);
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFailed, setShowFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [navigateTo, setNavigateTo] = useState(null);
  const [userId, setUserId] = useState(null);

  const user = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!navigateTo) return;

    setVerifying(true);
    const timer = setTimeout(() => {
      // navigate to leads 
      if (navigateTo === 'leads') {
        navigate('/leads');
      }
      window.location.reload();
    }, 1500);

    return () => clearTimeout(timer);
  }, [navigateTo, navigate]);

  useEffect(() => {
    const stateCompanyCode = location?.state?.companyCode || location?.state?.id;
    if (stateCompanyCode && !userId) {
      setUserId(String(stateCompanyCode));
    }
  }, [location, userId]);

  useEffect(() => {
    const loadMfaMode = async () => {
      try {
        const sessionResponse = await fetch(`${API_BASE}/leads/temp-auth`, {
          method: 'GET',
          credentials: 'include',
        });

        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          if (sessionData?.id) {
            setUserId(sessionData.id);
          }
        }

        const statusResponse = await fetch(`${API_BASE}/leads/mfa/setup-status`, {
          method: 'GET',
          credentials: 'include',
        });

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          if (statusData?.setupComplete) {
            setMode('verify');
            setInfoMessage('MFA is already set up. Enter the code from your authenticator app to continue.');
            return;
          }
        }

        const initResponse = await fetch(`${API_BASE}/leads/mfa/setup-init`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({}),
        });

        if (initResponse.status === 409) {
          setMode('verify');
          setInfoMessage('MFA is already set up. Enter the code from your authenticator app to continue.');
          return;
        }

        if (!initResponse.ok) {
          setMode('verify');
          return;
        }

        const initData = await initResponse.json();
        setUserId(initData?.id || null);
        setSetupData(initData);
        setMode('setup');
      } catch (error) {
        setMode('verify');
      }
    };

    loadMfaMode();
  }, []);

  const normalizeSixDigitCode = (value) => value.replace(/\D/g, '').slice(0, OTP_LENGTH);

  const resolveEffectiveUserId = async () => {
    if (userId) {
      return userId;
    }

    if (user?.id) {
      return String(user.id);
    }

    if (user?.companyCode) {
      const companyCode = String(user.companyCode);
      setUserId(companyCode);
      return companyCode;
    }

    const stateCompanyCode = location?.state?.companyCode || location?.state?.id;
    if (stateCompanyCode) {
      const companyCode = String(stateCompanyCode);
      setUserId(companyCode);
      return companyCode;
    }

    const sessionResponse = await fetch(`${API_BASE}/leads/temp-auth`, {
      method: 'GET',
      credentials: 'include',
    });

    if (sessionResponse.ok) {
      const sessionData = await sessionResponse.json();
      if (sessionData?.id) {
        const sessionId = String(sessionData.id);
        setUserId(sessionId);
        return sessionId;
      }
    }

    return null;
  };

  const routeByAccess = (data) => {
    const access = String(data.data || '').trim().toLowerCase();
    const pending = data.staffOnboardPending === true || data.staffOnboardPending === 1;

    if ((access === 'staff' || access === 'manager') && pending) {
      setNavigateTo('onboardstaff');
      return;
    }

    if (data.onboarding === false) {
      setNavigateTo('onboarding');
      return;
    }

    setNavigateTo(access);
  };

  const verifyForLogin = async (code) => {
    const effectiveUserId = await resolveEffectiveUserId();
    const fallbackCompanyCode = String(
      user?.companyCode || location?.state?.companyCode || location?.state?.id || ''
    ).trim();
    const requestId = String(effectiveUserId || fallbackCompanyCode || '').trim();

    if (!requestId) {
      throw new Error('Missing user session. Please sign in again.');
    }

    const response = await fetch(`${API_BASE}/leads/mfa/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        RandomCode: code,
        id: requestId,
        salesTemp: user?.salesTemp,
        companyCode: user?.companyCode || requestId,
      }),
    });

    if (response.status === 428) {
      setMode('setup');
      const initResponse = await fetch(`${API_BASE}/leads/mfa/setup-init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (initResponse.ok) {
        const initData = await initResponse.json();
        setUserId(initData?.id || null);
        setSetupData(initData);
      }
      return null;
    }

    if (!response.ok) {
      let message = 'Verification failed. Please try again.';
      try {
        const payload = await response.json();
        message = payload?.message || message;
      } catch (_error) {
        // Keep default message when response body is not JSON.
      }
      throw new Error(message);
    }

    return response.json();
  };

  const handleVerifySubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setShowFailed(false);
    setErrorMessage('');
    setVerifying(false);
    setNavigateTo(null);

    try {
      const data = await verifyForLogin(authCode);
      if (data?.success) {
        setInfoMessage('');
        routeByAccess(data);
      } else {
        setShowFailed(true);
        setErrorMessage('The code you entered is incorrect. Please try again.');
      }
    } catch (error) {
      setShowFailed(true);
      setErrorMessage(error?.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setShowFailed(false);
    setErrorMessage('');

    try {
      const confirmResponse = await fetch(`${API_BASE}/leads/mfa/setup-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ code: setupCode }),
      });

      if (!confirmResponse.ok) {
        let message = 'Invalid authenticator code. Please try again.';
        try {
          const payload = await confirmResponse.json();
          message = payload?.message || message;
        } catch (_error) {
          // Keep default message when response body is not JSON.
        }
        setShowFailed(true);
        setErrorMessage(message);
        return;
      }

      const confirmData = await confirmResponse.json();
      setBackupCodes(Array.isArray(confirmData?.backupCodes) ? confirmData.backupCodes : []);
      setMode('verify');
      setSetupCode('');
      setAuthCode('');
      setInfoMessage('MFA is set up. Enter a fresh 6-digit code from your authenticator app to continue.');
    } catch (error) {
      setShowFailed(true);
      setErrorMessage(error?.message || 'MFA setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!verifying && (
        <div className="relative min-h-screen flex bg-no-repeat bg-center bg-cover items-center justify-center overflow-hidden px-4 py-8 bg-slate-950"
             style={{ backgroundImage: "url('/nologo.png')" }}>

          
          <div className="pointer-events-none absolute -top-28 -left-20 h-[420px] w-[420px] rounded-full bg-indigo-600/20 blur-3xl" style={{ animation: 'mfaFloatA 24s ease-in-out infinite' }} />
          <div className="pointer-events-none absolute -bottom-24 -right-20 h-[380px] w-[380px] rounded-full bg-violet-600/20 blur-3xl" style={{ animation: 'mfaFloatB 28s ease-in-out infinite' }} />
          <div className="relative z-10 w-full max-w-md">
            <div className="mb-6 text-center">
              <img className="mx-auto h-14 w-14 drop-shadow-lg" src="/avenor.png" alt="Avenor Logo" loading="eager" />
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-white">Multi-Factor Authentication</h2>
              <p className="mt-2 text-sm text-slate-400">A code from your authenticator app is required to continue.</p>
            </div>

            <div className="rounded-3xl border border-white/10 bg-slate-900/75 px-6 py-7 shadow-2xl backdrop-blur-xl sm:px-8">
              <AuthenticatorBox />

              {mode === 'loading' && (
                <p className="mt-5 text-center text-sm text-slate-300">Loading authentication setup...</p>
              )}

              {mode === 'setup' && setupData && (
                <form onSubmit={handleSetupSubmit} className="mt-5 space-y-4">
                  <p className="text-sm leading-6 text-slate-300">
                    Scan the QR code with Google Authenticator or Microsoft Authenticator, then enter the 6-digit code below.
                  </p>
                  {setupData.qrCodeDataUrl && (
                    <div className="rounded-2xl border border-white/10 bg-white p-4 shadow-lg">
                      <img src={setupData.qrCodeDataUrl} alt="MFA QR Code" className="mx-auto h-52 w-52 rounded-xl" />
                    </div>
                  )}
                  {setupData.secret && (
                    <div className="rounded-2xl border border-indigo-400/20 bg-indigo-500/10 p-3 text-xs text-indigo-100 break-all">
                      Manual key: {setupData.secret}
                    </div>
                  )}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-white">Authenticator Code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={OTP_LENGTH}
                      value={setupCode}
                      onChange={(event) => setSetupCode(normalizeSixDigitCode(event.target.value))}
                      className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-center text-lg font-semibold tracking-[0.3em] text-white outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                      placeholder="123456"
                      aria-label="Authenticator code"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || setupCode.replace(/\D/g, '').length !== OTP_LENGTH}
                    className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                      loading || setupCode.replace(/\D/g, '').length !== OTP_LENGTH
                        ? 'cursor-not-allowed bg-indigo-500/40'
                        : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
                    }`}
                  >
                    {loading ? 'Confirming...' : 'Confirm & Continue'}
                  </button>
                </form>
              )}

              {mode === 'verify' && (
                <form onSubmit={handleVerifySubmit} className="mt-5 space-y-4">
                  <p className="text-sm leading-6 text-slate-300">
                    Enter the 6-digit code from your authenticator app, or a backup code if needed.
                  </p>
                  {infoMessage && (
                    <div className="rounded-xl border border-emerald-300/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">
                      {infoMessage}
                    </div>
                  )}
                  <div>
                    <label className="mb-2 block text-sm font-medium text-white">Authentication Code</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={OTP_LENGTH}
                      value={authCode}
                      onChange={(event) => setAuthCode(normalizeSixDigitCode(event.target.value))}
                      className="h-12 w-full rounded-xl border border-white/10 bg-white/5 px-4 text-center text-lg font-semibold tracking-[0.3em] text-white outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/40"
                      placeholder="123456"
                      aria-label="Authentication code"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || authCode.replace(/\D/g, '').length !== OTP_LENGTH}
                    className={`w-full rounded-xl py-3 text-sm font-semibold text-white transition shadow-lg ${
                      loading || authCode.replace(/\D/g, '').length !== OTP_LENGTH
                        ? 'cursor-not-allowed bg-indigo-500/40'
                        : 'bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500'
                    }`}
                  >
                    {loading ? 'Verifying...' : 'Verify'}
                  </button>
                </form>
              )}

              {backupCodes.length > 0 && (
                <div className="mt-5 rounded-2xl border border-amber-300/20 bg-amber-500/10 p-4 text-amber-100">
                  <p className="text-sm font-semibold">Save these backup codes now</p>
                  <p className="mt-1 text-xs text-amber-100/80">They are shown once and can be used if you lose access to your authenticator app.</p>
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {backupCodes.map((code) => (
                      <div key={code} className="rounded-lg border border-white/10 bg-slate-950/60 px-3 py-2 font-mono text-sm tracking-wide text-white">
                        {code}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {verifying && (
        <div className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-8 bg-slate-950">
          <div className="pointer-events-none absolute -top-28 -left-20 h-[420px] w-[420px] rounded-full bg-indigo-600/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -right-20 h-[380px] w-[380px] rounded-full bg-violet-600/20 blur-3xl" />
          <div className="relative z-10 w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/75 px-8 py-10 text-center shadow-2xl backdrop-blur-xl">
            <img className="mx-auto mb-5 h-14 w-14 animate-spin drop-shadow-lg" src="/avenor.png" alt="InsightOS Logo" loading="eager" />
            <h2 className="text-2xl font-semibold text-white mb-2 text-center">Verifying...</h2>
            <p className="text-sm text-slate-300 text-center">Please wait while we sign you in.</p>
          </div>
        </div>
      )}

      {showFailed && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="bg-red-600 text-white p-6 rounded-lg shadow-lg max-w-sm w-full">
            <h2 className="text-xl font-semibold mb-3">Verification Failed</h2>
            <p className="mb-4">{errorMessage || 'Authentication failed. Please try again.'}</p>
            <button
              onClick={() => setShowFailed(false)}
              className="bg-white text-red-600 px-4 py-2 rounded-md hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
